import {type Node, type Edge, applyNodeChanges, applyEdgeChanges, NodeChange, EdgeChange} from 'reactflow';
import {ActorRefFrom, assertEvent, assign, fromPromise, setup} from 'xstate';

import {EntityMapEntityNodeOrInaccessibleInfoNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';

import {fetchEntityMapGraph} from './EntityMapDAO';
import {GraphNodesAndEdgesById, parseEntityMapIntoGraphNodesAndEdgesById} from './EntityMapUtils';
import {EntityNodeMachine} from './EntityNodeMachine';

const INITIAL_DEPTH_ON_LOAD = 3;

const mergeMaps = <K, V>(maps: Array<Map<K, V>>): Map<K, V> =>
  new Map([...maps.flatMap((map) => Array.from(map.entries()))]);

interface EntityMapInput {
  entityId: string;
}

interface EntityMapContext extends GraphNodesAndEdgesById {
  entityId: string;
  spawnedNodeMachines: Map<string, ActorRefFrom<typeof EntityNodeMachine>>;
}

type EntityMapEvents =
  | {
      type: 'onNodesChange';
      changes: Array<NodeChange>;
    }
  | {
      type: 'onEdgesChange';
      changes: Array<EdgeChange>;
    }
  | {
      type: 'completedFetchingExtendedGraph';
      graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>;
      graphEdgesById: Map<string, Edge>;
    };

/**
 * XState state machine for managing the entity map graph for a given entityId.
 * Handles idle, initial loading, and error states.
 * Spawns child entity node machines for each entity in the graph, and stores them in context keyed by its
 * entityId. Listens for completedFetchingExtendedGraph events, which are sent from the child entity node
 * machines when they have loaded their relationships.
 * Stores graphNodesById and graphEdgesById in context.
 *
 * The idle state handles onNodesChange and onEdgesChange events, which can be sent from the view using
 * actorRef.send().
 */
export const EntityMapMachine = setup({
  types: {
    input: {} as EntityMapInput,
    context: {} as EntityMapContext,
    events: {} as EntityMapEvents,
  },
  actors: {
    loadInitialGraph: fromPromise<GraphNodesAndEdgesById, {entityId: string}>(async ({input: {entityId}}) => {
      const {entities, edges} = await fetchEntityMapGraph(entityId, INITIAL_DEPTH_ON_LOAD);
      return parseEntityMapIntoGraphNodesAndEdgesById({entities, edges});
    }),
  },
  actions: {
    mergeGraph: assign(({spawn, context}, params: GraphNodesAndEdgesById) => {
      const graphNodesById = mergeMaps([context.graphNodesById, params.graphNodesById]);
      const graphEdgesById = mergeMaps([context.graphEdgesById, params.graphEdgesById]);

      const spawnedNodeMachines = new Map(context.spawnedNodeMachines);
      const newEntityIds = Array.from(params.graphNodesById.keys()).filter(
        (entityId) => !spawnedNodeMachines.has(entityId)
      );
      for (const entityId of newEntityIds) {
        const entity = graphNodesById.get(entityId)!.data;
        const entityNodeMachine = spawn(EntityNodeMachine, {
          input: {entityId, entity},
        });
        spawnedNodeMachines.set(entityId, entityNodeMachine);
      }

      return {
        graphNodesById,
        graphEdgesById,
        spawnedNodeMachines,
      };
    }),
    layout: assign(({context}) => {
      const nodes = [...context.graphNodesById.values()];
      const edges = [...context.graphEdgesById.values()];

      const {nodes: layoutNodes, edges: layoutEdges} = layoutFlowchart({nodes, edges});
      const layoutedGraphNodesById = new Map(layoutNodes.map((node) => [node.id, node]));
      const layoutedGraphEdgesById = new Map(layoutEdges.map((edge) => [edge.id, edge]));

      return {
        graphNodesById: layoutedGraphNodesById,
        graphEdgesById: layoutedGraphEdgesById,
      };
    }),
    onNodesChange: assign(({context, event}) => {
      assertEvent(event, 'onNodesChange');
      const nodes: Array<Node> = [...context.graphNodesById.values()];
      const updatedNodes = applyNodeChanges(event.changes, nodes);
      return {
        graphNodesById: new Map(updatedNodes.map((node) => [node.id, node])),
      };
    }),
    onEdgesChange: assign(({context, event}) => {
      assertEvent(event, 'onEdgesChange');
      const edges: Array<Edge> = [...context.graphEdgesById.values()];
      const updatedEdges = applyEdgeChanges(event.changes, edges);
      return {
        graphEdgesById: new Map(updatedEdges.map((edge) => [edge.id, edge])),
      };
    }),
  },
}).createMachine({
  id: 'entityMapMachine',
  context: ({input}) => ({
    entityId: input.entityId,
    graphNodesById: new Map(),
    graphEdgesById: new Map(),
    spawnedNodeMachines: new Map(),
  }),
  initial: 'loading',
  states: {
    loading: {
      invoke: {
        id: 'loadInitialGraphActorFromLoadingState',
        input: ({context}) => ({entityId: context.entityId}),
        src: 'loadInitialGraph',
        onDone: {
          target: 'idle',
          actions: [
            {
              type: 'mergeGraph',
              params: ({event}) => ({
                graphNodesById: event.output.graphNodesById,
                graphEdgesById: event.output.graphEdgesById,
              }),
            },
            'layout',
          ],
        },
        onError: {
          target: 'error',
        },
      },
    },
    idle: {
      on: {
        onNodesChange: {
          actions: ['onNodesChange', 'layout'],
        },
        onEdgesChange: {
          actions: ['onEdgesChange', 'layout'],
        },
        completedFetchingExtendedGraph: {
          actions: [
            {
              type: 'mergeGraph',
              params: ({event}) => ({
                graphNodesById: event.graphNodesById,
                graphEdgesById: event.graphEdgesById,
              }),
            },
            'layout',
          ],
        },
      },
    },
    error: {},
  },
});
